(() => {
  if (window.efaSpecificProductsInit) { window.efaSpecificProductsInit(document); return; }
  const cleanups = new WeakMap();
  const gridCleanups = new WeakMap();
  const initGrid = grid => {
    if (gridCleanups.has(grid)) return;
    let frame;
    const place = () => {
      frame = null;
      const rows = [...grid.children].filter(card => card.matches('[data-specific-grid-row]'));
      const cards = [...grid.children].filter(card => !card.matches('[data-specific-grid-row]'));
      if (!rows.length || !cards.length) return;
      // Measure the actual rendered rows, including colour siblings and editorial cards.
      // Exclude inserted blocks during measurement so an old mobile position cannot
      // split a desktop product row when the viewport or grid density changes.
      const displays = rows.map(row => ({value:row.style.getPropertyValue('display'), priority:row.style.getPropertyPriority('display')}));
      rows.forEach(row => row.style.setProperty('display', 'none', 'important'));
      const lines = [];
      cards.forEach(card => {
        const top = Math.round(card.getBoundingClientRect().top);
        if (!lines.length || Math.abs(lines[lines.length - 1].top - top) > 2) lines.push({top, card});
      });
      rows.forEach((row, index) => {
        if (displays[index].value) row.style.setProperty('display', displays[index].value, displays[index].priority);
        else row.style.removeProperty('display');
      });
      rows.sort((a, b) => Number(a.dataset.specificGridRow) - Number(b.dataset.specificGridRow));
      const groups = new Map();
      let productLine = 0;
      let renderedLine = 1;
      rows.forEach(row => {
        const line = Math.max(1, Number(row.dataset.specificGridRow) || 1);
        productLine += Math.max(0, line - renderedLine);
        const before = lines[productLine]?.card || null;
        renderedLine = Math.max(line, renderedLine) + 1;
        if (!groups.has(before)) groups.set(before, []);
        groups.get(before).push(row);
      });
      groups.forEach((group, before) => {
        let anchor = before;
        group.slice().reverse().forEach(row => {
          if (row.nextElementSibling !== anchor) grid.insertBefore(row, anchor);
          anchor = row;
        });
      });
    };
    const request = () => { if (!frame) frame = requestAnimationFrame(place); };
    const resize = new ResizeObserver(request);
    resize.observe(grid);
    const mutation = new MutationObserver(request);
    mutation.observe(grid, {childList:true});
    gridCleanups.set(grid, () => {resize.disconnect(); mutation.disconnect(); cancelAnimationFrame(frame); gridCleanups.delete(grid);});
    place();
  };
  const init = (scope) => {
    scope.querySelectorAll('#collection').forEach(initGrid);
    const roots = [...scope.querySelectorAll('[data-efa-specific]')];
    if (scope.matches?.('[data-efa-specific]')) roots.unshift(scope);
    roots.forEach(root => {
      if (cleanups.has(root)) return;
      // EFA 17/10/2026 (Timothee) : trois affichages par appareil (grid, scroll, rotate), plusieurs listes (vignette a gauche,
      // au centre ou a droite), vitesse reglable, et sous 1001 px un seul carrousel (le layout) dont la vignette est la 1re diapositive.
      const lists = [...root.querySelectorAll('[data-specific-list]')];
      const layout = root.querySelector('.efa-specific__layout');
      if (!lists.length || !layout) return;
      root.setAttribute('data-js', '');
      const controls = root.querySelector('.efa-specific__controls');
      const prev = root.querySelector('[data-specific-prev]');
      const next = root.querySelector('[data-specific-next]');
      const pause = root.querySelector('[data-specific-pause]');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = matchMedia('(max-width: 760px)');
      const compact = matchMedia('(max-width: 1000px)');
      const abort = new AbortController();
      const options = {signal: abort.signal};
      let hoveredCard = null;
      let timer, visible = false, hovering = false, focusing = false, chosenPause = false, editorSelected = false, deferUntil = 0;
      const animations = new Map();
      const speed = () => Math.min(3000, Math.max(150, (Number(root.dataset.speed) || 0.6) * 1000));
      const mode = () => mobile.matches ? root.dataset.mobileMode : root.dataset.mode;

      // Carrousel
      const cancelAnimation = scroller => {
        cancelAnimationFrame(animations.get(scroller)); animations.delete(scroller); scroller.style.removeProperty('scroll-snap-type');
      };
      const cancelAll = () => [...animations.keys()].forEach(cancelAnimation);
      const animateTo = (scroller, position) => {
        cancelAnimation(scroller);
        const start = scroller.scrollLeft;
        const started = performance.now();
        const duration = speed();
        scroller.style.scrollSnapType = 'none';
        const step = now => {
          const progress = Math.min(1, (now - started) / duration);
          const eased = progress < .5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          scroller.scrollTo({left:start + (position - start) * eased, behavior:'instant'});
          if (progress < 1) animations.set(scroller, requestAnimationFrame(step));
          else {cancelAnimation(scroller); wrapAll();}
        };
        animations.set(scroller, requestAnimationFrame(step));
      };
      // Sous 1001 px, le defilement est porte par le layout (vignette en 1re carte) ; en « carrousel sur 2 rangees » mobile,
      // par les rails (bandeau au-dessus, cartes sur 2 rangees).
      const rails = root.querySelector('.efa-specific__rails');
      const isScroll = () => mode() === 'scroll' || mode() === 'scroll2';
      const scrollers = () => compact.matches ? [mode() === 'scroll2' ? rails : layout] : lists;
      const max = scroller => Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const movable = scroller => scroller.scrollWidth > scroller.clientWidth + 2;
      const canMove = () => isScroll() && scrollers().some(movable);
      const go = (direction, auto = false) => {
        if (!canMove()) return;
        scrollers().filter(movable).forEach(scroller => {
          const card = scroller.querySelector('[data-specific-list] > li');
          if (!card) return;
          const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
          const distance = card.getBoundingClientRect().width + gap;
          let position = scroller.scrollLeft + direction * distance;
          // Sans boucle possible (cartes qui ne remplissent pas des colonnes entieres) : aller-retour, jamais de rembobinage.
          if (auto && !loops.has(scroller)) {
            let dir = drift.dir.get(scroller) || 1;
            if (dir > 0 && scroller.scrollLeft >= max(scroller) - 2) dir = -1;
            else if (dir < 0 && scroller.scrollLeft <= 2) dir = 1;
            drift.dir.set(scroller, dir);
            position = scroller.scrollLeft + dir * distance;
          }
          position = Math.min(max(scroller), Math.max(0, position));
          if (auto && !motion.matches) animateTo(scroller, position);
          else {cancelAnimation(scroller); scroller.scrollTo({left:position, behavior:motion.matches ? 'instant' : 'smooth'});}
        });
      };

      // Grille animee : K cellules visibles ; a chaque pas UNE cellule passe en fondu a la carte suivante de la file.
      // Ordre de visite des cellules : alternance des extremites (2 x 2 : 1, 4, 2, 3 — en diagonale). La carte sortante
      // repart en fin de file, donc chaque produit de la liste passe le meme temps a l ecran.
      const rotation = {slots: [], queue: [], order: [], tick: 0, timers: new Set()};
      const cards = () => lists.flatMap(list => [...list.children]).filter(card => !card.hasAttribute('data-clone'));
      const slotCount = () => Number(compact.matches ? (mobile.matches ? root.dataset.slotsM : root.dataset.slotsT) : root.dataset.slotsD) || 4;
      const resetRotation = () => {
        rotation.timers.forEach(clearTimeout); rotation.timers.clear(); rotation.busy = false;
        cards().forEach(card => card.getAnimations({subtree: true}).forEach(animation => animation.cancel()));
        cards().forEach(card => {card.removeAttribute('data-slot'); card.classList.remove('is-entering', 'is-leaving');});
        rotation.slots = []; rotation.queue = []; rotation.tick = 0;
        if (mode() !== 'rotate') return;
        const all = cards();
        const k = Math.min(slotCount(), all.length);
        rotation.slots = all.slice(0, k);
        rotation.queue = all.slice(k);
        rotation.slots.forEach((card, index) => card.setAttribute('data-slot', String(index)));
        rotation.order = [];
        for (let a = 0, b = k - 1; a <= b;) {rotation.order.push(a++); if (a <= b) rotation.order.push(b--);}
      };
      const canRotate = () => mode() === 'rotate' && rotation.queue.length > 0;
      // GRILLE ANIMEE — echange « fade through » (18/10/2026, apres audit : fondu croise brouillon, textes superposes, rythme
      // irregulier). Modele : Material « fade through » (sortie PUIS entree, legere echelle de l element entrant). On n anime que
      // transform et opacity (composites), par l API Web Animations : sequence precise, annulable, sans classe a orchestrer.
      // 1. sortie courte, acceleree : la carte s efface et se tasse a 98 % ;
      // 2. entree de la photo, deceleree : fondu + « pose » de 104 % a 100 % ;
      // 3. le texte arrive en cascade (pastilles, marque, nom, prix) quand l ancien a disparu : jamais deux textes superposes.
      // La photo entrante est prechargee et decodee AVANT l echange : jamais de case blanche.
      const EASE_IN = 'cubic-bezier(.4,0,1,1)', EASE_OUT = 'cubic-bezier(.22,.61,.36,1)';
      const ready = card => {
        const img = card.querySelector(':scope > figure img');
        if (!img) return Promise.resolve();
        if (img.loading === 'lazy') img.loading = 'eager';
        if (img.complete && img.naturalWidth) return Promise.resolve();
        const decode = img.decode ? img.decode().catch(() => {}) : new Promise(done => img.addEventListener('load', done, {once: true}));
        return Promise.race([decode, new Promise(done => setTimeout(done, 1500))]);
      };
      const rotate = async () => {
        if (!canRotate() || rotation.busy) return;
        rotation.busy = true;
        let slot = rotation.order[rotation.tick++ % rotation.order.length];
        for (let tries = 1; rotation.slots[slot] === hoveredCard && tries < rotation.order.length; tries++) slot = rotation.order[rotation.tick++ % rotation.order.length];
        if (rotation.slots[slot] === hoveredCard) {rotation.busy = false; return;}
        const outgoing = rotation.slots[slot];
        const incoming = rotation.queue[0];
        await ready(incoming);
        if (mode() !== 'rotate' || rotation.slots[slot] !== outgoing) {rotation.busy = false; return;}
        rotation.queue.shift();
        rotation.queue.push(outgoing);
        rotation.slots[slot] = incoming;
        const D = speed() * 1.2;
        const animations = [];
        outgoing.classList.add('is-leaving');
        incoming.classList.add('is-entering');
        incoming.setAttribute('data-slot', String(slot));
        animations.push(outgoing.animate([{opacity: 1}, {opacity: 0}], {duration: .35 * D, easing: EASE_IN, fill: 'forwards'}));
        const outPhoto = outgoing.querySelector(':scope > figure');
        if (outPhoto) animations.push(outPhoto.animate([{transform: 'scale(1)'}, {transform: 'scale(.98)'}], {duration: .35 * D, easing: EASE_IN, fill: 'forwards'}));
        const inPhoto = incoming.querySelector(':scope > figure');
        if (inPhoto) animations.push(inPhoto.animate([{opacity: 0, transform: 'scale(1.04)'}, {opacity: 1, transform: 'scale(1)'}], {duration: .75 * D, delay: .3 * D, easing: EASE_OUT, fill: 'both'}));
        const lines = [...incoming.querySelectorAll(':scope > .text-start > *')];
        lines.forEach((line, index) => animations.push(line.animate([{opacity: 0, transform: 'translateY(8px)'}, {opacity: 1, transform: 'translateY(0)'}], {duration: .5 * D, delay: (.45 + index * .07) * D, easing: EASE_OUT, fill: 'both'})));
        // Precharge de la carte suivante, pour le prochain echange.
        if (rotation.queue[0]) ready(rotation.queue[0]);
        await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
        animations.forEach(animation => animation.cancel());
        outgoing.classList.remove('is-leaving');
        incoming.classList.remove('is-entering');
        if (rotation.slots[slot] !== outgoing) outgoing.removeAttribute('data-slot');
        rotation.busy = false;
        measure();
      };


      // BOUCLE SANS FIN (18/10/2026) : sur ordinateur, un carrousel automatique (et le cover flow) recoit un jeu de clones avant
      // et apres ses cartes ; quand on s eloigne des originaux d un demi-jeu, on recale d un jeu entier, invisiblement.
      // Plus de rembobinage. Condition : le nombre de cartes doit remplir des colonnes entieres (2 rangees en grande vignette).
      const loops = new Map();
      const rows = () => root.dataset.size === 'large' ? 2 : 1;
      const cloneCard = card => {
        const copy = card.cloneNode(true);
        copy.setAttribute('data-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('id');
        copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
        return copy;
      };
      const unclone = list => {
        list.querySelectorAll(':scope > [data-clone]').forEach(clone => clone.remove());
        [...list.children].forEach(card => {card.style.removeProperty('--t'); card.style.removeProperty('transform'); card.style.removeProperty('z-index');});
        loops.delete(list);
      };
      const enclone = list => {
        const originals = [...list.children];
        if (originals.length < 2 || originals.length % rows()) return false;
        list.prepend(...originals.map(cloneCard));
        list.append(...originals.map(cloneCard));
        loops.set(list, originals.length);
        return true;
      };
      const loopSet = list => {
        const n = loops.get(list);
        return n ? list.children[n].offsetLeft - list.children[0].offsetLeft : 0;
      };
      const jump = (list, left) => {
        list.style.scrollSnapType = 'none';
        list.scrollTo({left, behavior: 'instant'});
        if (!root.hasAttribute('data-drifting')) list.style.removeProperty('scroll-snap-type');
      };
      const wrap = list => {
        const set = loopSet(list);
        if (!set) return false;
        const left = list.scrollLeft;
        const target = left < set * .5 ? left + set : left > set * 1.5 ? left - set : left;
        if (target === left) return false;
        jump(list, target);
        drift.pos.set(list, target);
        return true;
      };
      const wrapAll = () => {if (animations.size) return; let moved = false; loops.forEach((n, list) => {moved = wrap(list) || moved;}); if (moved) paintCover();};

      // Cover flow (carrousel ordinateur + vignette au centre) : la liste defile derriere la vignette fixe ; chaque carte est
      // placee sur un arc selon sa distance au centre (p = -1 bord gauche, 0 centre, 1 bord droit).
      // References : « Cover Flow » de Bramus Van Damme (scroll-driven-animations.style) et Addy Osmani (addyosmani.com/blog/coverflow).
      // Calcul en JS plutot qu en animation-timeline : Firefox ne livre pas encore les animations pilotees par le defilement, et le
      // defilement automatique est deja pilote ici.
      const cover = {on: false, frame: 0, settle: 0};
      const isCover = () => root.hasAttribute('data-coverflow') && !compact.matches && mode() === 'scroll';
      // v6 : la perspective est une animation CSS pilotee par le defilement. Le JS ne la calcule QUE si le navigateur ne gere
      // pas les view timelines (Firefox) : il fixe alors --t = position de la carte de 0 (entre a droite) a 1 (sort a gauche).
      const nativeCover = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));
      const paintCover = () => {
        cover.frame = 0;
        if (!cover.on || nativeCover) return;
        const list = lists[0];
        const width = list.clientWidth;
        if (!width) return;
        const base = list.firstElementChild.offsetLeft;
        for (const card of list.children) {
          const left = card.offsetLeft - base - list.scrollLeft;
          const t = Math.min(1, Math.max(0, (width - left) / (width + card.offsetWidth)));
          card.style.setProperty('--t', t.toFixed(4));
        }
      };
      const requestCover = () => {if (cover.on && !cover.frame) cover.frame = requestAnimationFrame(paintCover);};

      // Installation des boucles selon l etat courant (largeur, affichage, carrousel automatique).
      const setupLoops = () => {
        lists.forEach(unclone);
        cover.on = false;
        drift.pos.clear();
        if (compact.matches || mode() !== 'scroll') return;
        if (isCover()) {
          const list = lists[0];
          const first = list.firstElementChild;
          const second = first && first.nextElementSibling;
          if (!second || !enclone(list)) return;
          cover.on = true;
          const step = second.offsetLeft - first.offsetLeft;
          // 1re carte originale juste a droite de la vignette : le defilement l amene ensuite derriere elle, de droite a gauche.
          jump(list, first.offsetLeft - list.firstElementChild.offsetLeft + first.offsetWidth / 2 - list.clientWidth / 2 - step);
          paintCover();
          return;
        }
        if (root.dataset.auto !== 'true') return;
        lists.forEach(list => {
          if (list.scrollWidth <= list.clientWidth + 2) return;
          const first = list.firstElementChild;
          if (!enclone(list)) return;
          jump(list, first.offsetLeft - list.firstElementChild.offsetLeft);
        });
      };

      // DEFILEMENT CONTINU (18/10/2026, Timothee : « un reglage pour sa vitesse d execution ») : les cartes glissent a vitesse
      // constante (px/s), sans a-coups ; arret au survol, au focus, hors ecran, bouton pause, mouvement reduit ; une action du
      // visiteur (fleche, molette, glisser) suspend 3 s puis reprend d ou il s est arrete. Sans boucle possible : aller-retour.
      const drift = {raf: 0, last: 0, pos: new Map(), dir: new Map(), factor: 1};
      const autoHere = () => root.dataset.auto === 'true' && (!mobile.matches || root.dataset.autoMobile === 'true');
      const driftOn = () => autoHere() && root.dataset.autoMode === 'continuous' && isScroll();
      const driftStep = now => {
        drift.raf = requestAnimationFrame(driftStep);
        const dt = drift.last ? Math.min(64, now - drift.last) : 16;
        drift.last = now;
        if (Date.now() < deferUntil || animations.size) {drift.pos.clear(); return;}
        // Survol a la souris : la vitesse glisse vers 35 % (constante de temps ~300 ms), jamais d arret sec ; les cartes restent
        // faciles a viser et le mouvement ne s interrompt pas.
        drift.factor += ((hovering ? .35 : 1) - drift.factor) * (1 - Math.exp(-dt / 300));
        const distance = (Number(root.dataset.drift) || 35) * drift.factor * dt / 1000;
        scrollers().filter(movable).forEach(scroller => {
          let pos = drift.pos.has(scroller) ? drift.pos.get(scroller) : scroller.scrollLeft;
          if (Math.abs(pos - scroller.scrollLeft) > 4) pos = scroller.scrollLeft;
          if (loops.has(scroller)) {
            pos += distance;
            const set = loopSet(scroller);
            if (set && pos > set * 1.5) pos -= set;
          } else {
            const dir = drift.dir.get(scroller) || 1;
            pos += dir * distance;
            if (pos >= max(scroller)) {pos = max(scroller); drift.dir.set(scroller, -1);}
            if (pos <= 0) {pos = 0; drift.dir.set(scroller, 1);}
          }
          drift.pos.set(scroller, pos);
          scroller.scrollLeft = pos;
        });
      };
      const startDrift = () => {
        if (drift.raf) return;
        root.setAttribute('data-drifting', '');
        drift.last = 0;
        drift.raf = requestAnimationFrame(driftStep);
      };
      const stopDrift = () => {
        if (!drift.raf) return;
        cancelAnimationFrame(drift.raf);
        drift.raf = 0;
        drift.pos.clear();
        root.removeAttribute('data-drifting');
      };
      const animated = () => mode() === 'rotate' ? canRotate() : (autoHere() && canMove());
      const measure = () => root.style.setProperty('--specific-card-height', `${Math.max(0, ...cards().filter(card => card.offsetParent !== null && !card.classList.contains('is-leaving')).map(card => card.getBoundingClientRect().height))}px`);
      const refresh = () => {
        controls.hidden = !canMove();
        pause.hidden = !animated() || motion.matches;
        const active = scrollers().filter(movable);
        const looping = active.some(scroller => loops.has(scroller));
        prev.disabled = !looping && (!active.length || active.every(scroller => scroller.scrollLeft <= 2));
        next.disabled = !looping && (!active.length || active.every(scroller => scroller.scrollLeft >= max(scroller) - 2));
      };
      const stop = () => {clearTimeout(timer); timer = null;};
      const schedule = () => {
        // 18/10/2026 (Timothee) : le SURVOL n arrete plus l animation (arret au survol : absent au toucher, peu decouvrable, non exige
        // par WCAG 2.2.2, qui demande un moyen VISIBLE d arreter : le bouton pause). Le survol ralentit le defilement continu et protege
        // la carte survolee dans la grille animee. Restent : bouton pause, focus clavier (W3C APG), hors ecran, onglet cache, editeur,
        // mouvement reduit.
        if (!animated() || motion.matches || !visible || focusing || chosenPause || editorSelected || document.hidden) {stop(); stopDrift(); return;}
        if (driftOn()) {stop(); startDrift(); return;}
        stopDrift();
        if (timer) return;
        timer = setTimeout(() => {
          timer = null;
          if (Date.now() >= deferUntil) {if (mode() === 'rotate') rotate(); else go(1, true);}
          schedule();
        }, Math.max(1000, Number(root.dataset.interval) * 1000 || 4000, deferUntil - Date.now()) + speed());
      };
      prev.addEventListener('click', () => {deferUntil = Date.now() + (driftOn() ? 3000 : 2000); go(-1);}, options);
      next.addEventListener('click', () => {deferUntil = Date.now() + (driftOn() ? 3000 : 2000); go(1);}, options);
      pause.addEventListener('click', event => {
        event.preventDefault();
        chosenPause = !chosenPause;
        pause.setAttribute('aria-pressed', String(chosenPause));
        pause.setAttribute('aria-label', chosenPause ? pause.dataset.playLabel : pause.dataset.pauseLabel);
        schedule();
      }, options);
      root.addEventListener('pointerenter', event => {if (event.pointerType === 'mouse') hovering = true;}, options);
      root.addEventListener('pointerleave', event => {if (event.pointerType === 'mouse') {hovering = false; hoveredCard = null;}}, options);
      root.addEventListener('pointerover', event => {if (event.pointerType === 'mouse') hoveredCard = event.target.closest?.('[data-specific-list] > li') || null;}, options);
      root.addEventListener('shopify:block:select', () => {editorSelected = true; schedule();}, options);
      root.addEventListener('shopify:block:deselect', () => {editorSelected = false; schedule();}, options);
      root.addEventListener('focusin', event => {if (event.target !== pause) {focusing = true; schedule();}}, options);
      root.addEventListener('focusout', () => {queueMicrotask(() => {focusing = root.contains(document.activeElement) && document.activeElement !== pause; schedule();});}, options);
      [...lists, layout, rails].forEach(scroller => {
        scroller.addEventListener('pointerdown', () => {cancelAnimation(scroller); deferUntil = Date.now() + (driftOn() ? 3000 : 5000);}, options);
        scroller.addEventListener('wheel', () => {deferUntil = Date.now() + 3000;}, {...options, passive: true});
        scroller.addEventListener('scroll', () => {
          refresh(); requestCover();
          clearTimeout(cover.settle); cover.settle = setTimeout(wrapAll, 140);
        }, {...options, passive: true});
      });
      document.addEventListener('visibilitychange', schedule, options);
      const resize = new ResizeObserver(() => {measure(); refresh(); requestCover(); schedule();});
      lists.forEach(list => resize.observe(list));
      resize.observe(root);
      const intersection = new IntersectionObserver(entries => {visible = entries[0].isIntersecting; schedule();});
      intersection.observe(root);
      const changed = () => {stop(); stopDrift(); cancelAll(); resetRotation(); setupLoops(); measure(); refresh(); schedule();};
      motion.addEventListener('change', changed);
      mobile.addEventListener('change', changed);
      compact.addEventListener('change', changed);
      cleanups.set(root, () => {
        stop(); stopDrift(); cancelAll(); rotation.timers.forEach(clearTimeout); clearTimeout(cover.settle); cancelAnimationFrame(cover.frame); abort.abort(); resize.disconnect(); intersection.disconnect();
        motion.removeEventListener('change', changed); mobile.removeEventListener('change', changed); compact.removeEventListener('change', changed); cleanups.delete(root);
      });
      // EFA 06/10/2026 : survol des pastilles couleur limite a SA carte.
      // Cause : custom-async.js (listScrollableAsync) parcourt `#collection.l4cl` avec querySelectorAll('li'), qui inclut
      // li.efa-specific-grid-row ; il attache alors a chaque pastille du bloc un mouseover qui ecrit data-img dans TOUTES les img
      // du bloc (autres cartes + vignette editoriale, jamais restauree car hors <picture>). On arrete l'evenement a la racine du bloc
      // (phase de capture, avant les ecouteurs poses sur la pastille) et on applique le meme echange d'image a la seule carte survolee.
      const memo = img => {
        if (!img.hasAttribute('data-src-initial') && img.hasAttribute('src')) img.setAttribute('data-src-initial', img.getAttribute('src'));
        if (!img.hasAttribute('data-srcset-initial') && img.hasAttribute('srcset')) img.setAttribute('data-srcset-initial', img.getAttribute('srcset'));
      };
      root.querySelectorAll('[data-specific-list] picture img').forEach(memo);
      root.addEventListener('mouseover', event => {
        const swatch = event.target.closest?.('.check.color li[data-img]');
        if (!swatch || !root.contains(swatch)) return;
        event.stopPropagation();
        const card = swatch.closest('[data-specific-list] > li');
        if (!card) return;
        const source = swatch.getAttribute('data-img');
        card.querySelectorAll('picture img').forEach(memo);
        card.querySelectorAll('img:not(.color-variant-img)').forEach(img => {
          if (img.closest('.check.color')) return;
          img.setAttribute('src', source);
          if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
        });
      }, {...options, capture: true});
      resetRotation();
      setupLoops();
      measure(); refresh(); schedule();
    });
  };
  window.efaSpecificProductsInit = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-efa-specific]').forEach(root => cleanups.get(root)?.()));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('#collection').forEach(grid => gridCleanups.get(grid)?.()));
  // Filtering and pagination replace the collection HTML without a section-load event.
  const changed = new MutationObserver(records => {
    records.forEach(record => {
      record.removedNodes.forEach(node => {
        if (node.nodeType !== 1 || node.isConnected) return;
        if (node.matches('[data-efa-specific]')) cleanups.get(node)?.();
        node.querySelectorAll('[data-efa-specific]').forEach(root => cleanups.get(root)?.());
        if (node.id === 'collection') gridCleanups.get(node)?.();
        node.querySelectorAll('#collection').forEach(grid => gridCleanups.get(grid)?.());
      });
      record.addedNodes.forEach(node => {
      if (node.nodeType !== 1) return;
      if (node.id === 'collection') initGrid(node);
      if (node.matches('[data-efa-specific]') || node.querySelector('[data-efa-specific]')) init(node);
      });
    });
  });
  changed.observe(document.documentElement, {childList:true, subtree:true});
})();

